@<template>
  <v-container fluid>
    <toolbar mode="application" :item="application">
      <v-spacer />
      <template v-if="!!item">
        <ax-menu v-if="!!selectedGroup"
                 icon="mdi-format-columns"
                 title="Visible Columns"
                 :options="columnVisibility.options"
                 :default="columnVisibility.selected"
                 mode="icon"
                 model="columnVisibility"
                 :multiple="true"
                 :mandatory="false"
                 @changed="onColumnVisibilityChange" />
        <v-btn icon @click="onExportEconomics" :disabled="!selectedGroup">
          <v-icon>mdi-download</v-icon>
        </v-btn>
        <!-- <ax-toggle icon="mdi-numeric" tooltip="Toggle thousand" :value="thousands" @clicked="thousands = $event"></ax-toggle> -->
        <v-btn icon @click="$router.push({ name: 'reportCreate' })" :disabled="!item">
          <v-icon>mdi-table-multiple</v-icon>
        </v-btn>
        <v-chip close @click:close="uuid = null">{{ item.name || item.uuid }}</v-chip>
      </template>
    </toolbar>
    <v-card v-if="item">
      <v-card-text>
        <splitpanes class="default-theme"
                    :class="{ dark: isDark }"
                    @resized="storePaneSize">
          <pane :size="paneSize[0].size"
                :max-size="paneSize[0].max"
                :min-size="paneSize[0].min">
            <!-- MAP -->
            <v-card flat
                    tile>
              <v-toolbar flat
                         tile
                         dense>
                <v-toolbar-title>Location Map ({{ totalWells | numberFormat(round=thousands, format='0,0a') }} wells)</v-toolbar-title>
                <v-spacer />
                <v-autocomplete :items="wells.data.map(w => w[API])"
                                v-model="selectedWell"
                                v-if="groupTechnique == 'forecasts'"
                                label=""
                                hide-details
                                clearable
                                prepend-inner-icon="mdi-api" />
              </v-toolbar>
              <vue-plotly id="map"
                          :data="charts.map.data"
                          :layout="charts.map.layout"
                          :options="charts.map.options"
                          @afterplot="charts.map.loaded = true" />
            </v-card>
            <!-- FILTERS -->
            <v-card flat
                    tile>
              <v-toolbar flat
                         tile
                         dense>
                <v-toolbar-title>Groups</v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <ax-groups :uuid="uuid" :groups="groups" :targets="targets.options.map(t => t.value)" :editable="false" />
              </v-card-text>
            </v-card>
            <v-divider />
            <v-card :disabled="!selectedGroup">
              <v-card-text>
                <v-row>
                  <v-col cols="4">
                    <v-toolbar flat
                               color="transparent"><v-toolbar-title>Start Date</v-toolbar-title></v-toolbar>
                    <v-text-field type="date"
                                  label="Aggregation Month"
                                  v-model:date="inputs.aggregation_month"
                                  :show-current="true"
                                  outlined
                                  dense
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Initial Capex"
                                  type="number"
                                  hide-spin-buttons
                                  hint="Once for each well"
                                  v-model.number="inputs.initial_capex"
                                  suffix="$"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Fixed Cost"
                                  type="number"
                                  hint="Per well per month"
                                  hide-spin-buttons
                                  v-model.number="inputs.fixed_cost"
                                  suffix="$"
                                  @change="onChangeInputs" />
                  </v-col>
                  <v-col cols="4">
                    <v-toolbar flat
                               color="transparent"><v-toolbar-title>Prices</v-toolbar-title></v-toolbar>
                    <v-text-field outlined
                                  dense
                                  label="Oil Price"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.oil_price"
                                  suffix="$"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Gas Price"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.gas_price"
                                  suffix="$"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Shrinkage"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.shrinkage"
                                  suffix="%"
                                  @change="onChangeInputs" />
                  </v-col>
                  <v-col cols="4">
                    <v-toolbar flat
                               color="transparent"><v-toolbar-title>Differentials</v-toolbar-title></v-toolbar>
                    <v-text-field outlined
                                  dense
                                  label="Oil Differential"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.oil_differential"
                                  suffix="$/bbl"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Gas Differential"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.gas_differential"
                                  suffix="$/mcf"
                                  @change="onChangeInputs" />
                  </v-col>
                  <v-col cols="4">
                    <v-toolbar flat
                               color="transparent"><v-toolbar-title>Expenses</v-toolbar-title></v-toolbar>
                    <v-text-field outlined
                                  dense
                                  label="Expense (Oil)"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.expense_oil"
                                  suffix="$/bbl"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Expense (Gas)"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.expense_gas"
                                  suffix="$/mcf"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Expense (Water)"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.expense_water"
                                  suffix="$/bbl"
                                  @change="onChangeInputs" />
                  </v-col>
                  <v-col cols="4">
                    <v-toolbar flat
                               color="transparent"><v-toolbar-title>Tax Rates</v-toolbar-title></v-toolbar>
                    <v-text-field outlined
                                  dense
                                  label="Severance Tax (Oil)"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.severance_tax_oil"
                                  suffix="%"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Severance Tax (Gas)"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.severance_tax_gas"
                                  suffix="%"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Ad Valorem Tax"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.ad_valorem_tax"
                                  suffix="%"
                                  @change="onChangeInputs" />
                  </v-col>
                  <v-col cols="4">
                    <v-toolbar flat
                               color="transparent"><v-toolbar-title>Interest Rates</v-toolbar-title></v-toolbar>
                    <v-text-field outlined
                                  dense
                                  label="Working Interest"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.working_interest"
                                  suffix="%"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Net Revenue Interest"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.net_revenue_interest"
                                  suffix="%"
                                  @change="onChangeInputs" />
                    <v-text-field outlined
                                  dense
                                  label="Discount Rate"
                                  type="number"
                                  hide-spin-buttons
                                  v-model.number="inputs.discount_rate"
                                  suffix="%"
                                  @change="onChangeInputs" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </pane>
          <pane :size="paneSize[1].size"
                :max-size="paneSize[1].max"
                :min-size="paneSize[1].min"
                style="height: auto">
            <template v-if="!!selectedWell">
              <v-card flat tile>
                <v-toolbar flat tile dense>
                  <v-toolbar-title>First Year Summary ({{ selectedWell }})</v-toolbar-title>
                  <v-spacer />
                  <!-- <ax-toggle label="Thousands"
                             v-model="thousands"
                             class="ml-4"
                             @change="onChangeThousands" /> -->
                  
                  <ax-button :icon="showFirst ? 'mdi-chevron-up' : 'mdi-chevron-down'" tooltip="Toggle first year summary" @clicked="showFirst = !showFirst" />
                </v-toolbar>
                <v-data-table dense
                              v-show="showFirst"
                              :items="single.data"
                              :headers="getVisibleHeaders(single.headers)"
                              :items-per-page="-1"
                              disable-sort
                              hide-default-footer>
                  <template v-slot:[`item.gross_oil`]="{ item }">{{ item.gross_oil | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.gross_gas`]="{ item }">{{ item.gross_gas | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.gross_water`]="{ item }">{{ item.gross_gas | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.gross_boe`]="{ item }">{{ item.gross_boe | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.net_oil`]="{ item }">{{ item.net_oil | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.net_gas`]="{ item }">{{ item.net_gas | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.oil_price`]="{ item }">{{ item.oil_price | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.gas_price`]="{ item }">{{ item.gas_price | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.oil_differential`]="{ item }">{{ item.oil_differential | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.gas_differential`]="{ item }">{{ item.gas_differential | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.realized_oil_price`]="{ item }">{{ item.realized_oil_price | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.realized_gas_price`]="{ item }">{{ item.realized_gas_price | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.oil_revenue`]="{ item }">{{ item.oil_revenue | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.gas_revenue`]="{ item }">{{ item.gas_revenue | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.gross_revenue`]="{ item }">{{ item.gross_revenue | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.operating_expenses`]="{ item }">{{ item.operating_expenses | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.severance_tax`]="{ item }">{{ item.severance_tax | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.ad_valorem_tax`]="{ item }">{{ item.ad_valorem_tax | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv0`]="{ item }">{{ item.pv0 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv10`]="{ item }">{{ item.pv10 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv15`]="{ item }">{{ item.pv15 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv20`]="{ item }">{{ item.pv20 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv30`]="{ item }">{{ item.pv30 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.net_income`]="{ item }">{{ item.net_income | numberFormat(round=thousands, format='0,0.0a') }}</template>
                </v-data-table>
              </v-card>
              <v-divider />
            </template>
            <template v-if="!!selectedGroup">
              <v-card flat tile>
                <v-toolbar flat tile dense>
                  <v-toolbar-title>Annual Summary ({{ selectedWell || selectedGroup.name }})</v-toolbar-title>
                  <v-spacer />
                  <!-- <ax-toggle icon="mdi-numeric" tooltip="Toggle thousand" :value="thousands" @clicked="thousands = $event"></ax-toggle> -->
                  <ax-button :icon="showAnnuals ? 'mdi-chevron-up' : 'mdi-chevron-down'" tooltip="Toggle annuals" @clicked="showAnnuals = !showAnnuals" />
                </v-toolbar>
                <v-data-table dense
                              v-show="showAnnuals"
                              :items="annuals.data"
                              :headers="getVisibleHeaders(annuals.headers)"
                              :items-per-page="-1"
                              disable-sort
                              hide-default-footer>
                  <template v-slot:[`item.gross_oil`]="{ item }">{{ item.gross_oil | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.gross_gas`]="{ item }">{{ item.gross_gas | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.gross_boe`]="{ item }">{{ item.gross_boe | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.net_oil`]="{ item }">{{ item.net_oil | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.net_gas`]="{ item }">{{ item.net_gas | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.oil_price`]="{ item }">{{ item.oil_price | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.gas_price`]="{ item }">{{ item.gas_price | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.oil_differential`]="{ item }">{{ item.oil_differential | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.gas_differential`]="{ item }">{{ item.gas_differential | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.realized_oil_price`]="{ item }">{{ item.realized_oil_price | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.realized_gas_price`]="{ item }">{{ item.realized_gas_price | numberFormat(format='0,0.00') }}</template>
                  <template v-slot:[`item.oil_revenue`]="{ item }">{{ item.oil_revenue | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.gas_revenue`]="{ item }">{{ item.gas_revenue | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.gross_revenue`]="{ item }">{{ item.gross_revenue | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.operating_expenses`]="{ item }">{{ item.operating_expenses | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.severance_tax`]="{ item }">{{ item.severance_tax | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.ad_valorem_tax`]="{ item }">{{ item.ad_valorem_tax | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv0`]="{ item }">{{ item.pv0 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv10`]="{ item }">{{ item.pv10 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv15`]="{ item }">{{ item.pv15 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv20`]="{ item }">{{ item.pv20 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.pv30`]="{ item }">{{ item.pv30 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  <template v-slot:[`item.net_income`]="{ item }">{{ item.net_income | numberFormat(round=thousands, format='0,0.0a') }}</template>
                </v-data-table>
              </v-card>
              <!-- Individual Wells -->
              <template v-if="!isEmpty(wells.data)">
                <v-divider />
                <v-card flat tile>
                  <v-toolbar flat tile dense>
                    <v-toolbar-title>Individual Wells ({{ selectedGroup.name }})</v-toolbar-title>
                    <v-spacer />
                    <ax-button :icon="showWells ? 'mdi-chevron-up' : 'mdi-chevron-down'" tooltip="Toggle individual wells" @clicked="showWells = !showWells" />
                  </v-toolbar>
                  <v-data-table dense
                                v-show="showWells"
                                :items="wells.data"
                                :headers="getVisibleHeaders(wellsHeaders)"
                                :items-per-page="-1"
                                hide-default-footer>
                    <template v-slot:[`item.gross_oil`]="{ item }">{{ item.gross_oil | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.gross_gas`]="{ item }">{{ item.gross_gas | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.gross_boe`]="{ item }">{{ item.gross_boe | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.net_oil`]="{ item }">{{ item.net_oil | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.net_gas`]="{ item }">{{ item.net_gas | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.oil_price`]="{ item }">{{ item.oil_price | numberFormat(format='0,0.00') }}</template>
                    <template v-slot:[`item.gas_price`]="{ item }">{{ item.gas_price | numberFormat(format='0,0.00') }}</template>
                    <template v-slot:[`item.oil_differential`]="{ item }">{{ item.oil_differential | numberFormat(format='0,0.00') }}</template>
                    <template v-slot:[`item.gas_differential`]="{ item }">{{ item.gas_differential | numberFormat(format='0,0.00') }}</template>
                    <template v-slot:[`item.realized_oil_price`]="{ item }">{{ item.realized_oil_price | numberFormat(format='0,0.00') }}</template>
                    <template v-slot:[`item.realized_gas_price`]="{ item }">{{ item.realized_gas_price | numberFormat(format='0,0.00') }}</template>
                    <template v-slot:[`item.oil_revenue`]="{ item }">{{ item.oil_revenue | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.gas_revenue`]="{ item }">{{ item.gas_revenue | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.gross_revenue`]="{ item }">{{ item.gross_revenue | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.operating_expenses`]="{ item }">{{ item.operating_expenses | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.severance_tax`]="{ item }">{{ item.severance_tax | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.ad_valorem_tax`]="{ item }">{{ item.ad_valorem_tax | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.pv0`]="{ item }">{{ item.pv0 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.pv10`]="{ item }">{{ item.pv10 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.pv15`]="{ item }">{{ item.pv15 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.pv20`]="{ item }">{{ item.pv20 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.pv30`]="{ item }">{{ item.pv30 | numberFormat(round=thousands, format='0,0.0a') }}</template>
                    <template v-slot:[`item.net_income`]="{ item }">{{ item.net_income | numberFormat(round=thousands, format='0,0.0a') }}</template>
                  </v-data-table>
                </v-card>
              </template>

              <!-- RATIOS -->
              <template v-if="!isEmpty(charts.ratio.data)">
                <v-divider />
                <v-card flat tile>
                  <v-divider />
                  <v-toolbar flat tile dense>
                    <v-toolbar-title>Gas/Oil Ratio ({{ selectedWell || selectedGroup.name }})</v-toolbar-title>
                    <v-spacer />
                    <ax-button :icon="showRatio ? 'mdi-chevron-up' : 'mdi-chevron-down'" tooltip="Toggle ratios" @clicked="showRatio = !showRatio" />
                  </v-toolbar>
                  <v-card-text>
                    <vue-plotly
                      id="ratios"
                      v-show="showRatio"
                      class="chart-container"
                      :data="charts.ratio.data"
                      :layout="charts.ratio.layout"
                      :options="charts.ratio.options"
                    />
                  </v-card-text>
                </v-card>
              </template>
            </template>
            <v-card-text v-else
                         class="pa-16 grey--text text-center"
                         :class="[isDark ? 'text--lighten' : 'text--darken']">
              <img src="@/assets/images/ax_logomark.svg"
                   width="256"
                   height="256"
                   contain
                   class="greyscale mt-16 mb-4" />
              <p>Select a well or a group of wells</p>
            </v-card-text>
          </pane>
        </splitpanes>
      </v-card-text>
    </v-card>
    <dlg-open-file v-else
                   mode="prediction"
                   @submitted="uuid = $event" />
  </v-container>
</template>
<script>
import "splitpanes/dist/splitpanes.css"
import { Splitpanes, Pane } from "splitpanes"
import AxButton from "@/components/button"
import AxMenu from "@/components/menu"
import AxToggle from "@/components/toggle"
import AxGroups from "@/components/Apps/Forecast/Groups"
import dlgOpenFile from "@/components/open"
import dlgRenameGroup from "@/components/Apps/Forecast/Dialogs/RenameGroup"
import Economics from "@/api/economics"
import Forecasting from "@/api/forecasting"
import Predictions from "@/api/predictions"
import toolbar from "@/components/toolbar"
import VuePlotly from "@statnett/vue-plotly"
import XLSX from "xlsx"
import DataFrame from "dataframe-js"
export default {
  props: ["application"],
  components: {
    AxButton,
    AxMenu,
    AxToggle,
    AxGroups,
    dlgOpenFile,
    dlgRenameGroup,
    Pane,
    Splitpanes,
    toolbar,
    VuePlotly,
  },
  data() {
    return {
      uuid: null,
      item: null,
      aside: false,
      predictions: {},
      frame: {
        items: {},
        table: [],
        headers: [],
        selected: [],
      },
      groups: {
        selected: undefined,
        list: [],
      },
      selectedWell: null,
      showAnnuals: true,
      showWells: true,
      showRatio: true,
      showFirst: true,
      thousands: false,
      lines: {
        options: [
          { text: "P90", value: "p90", type: "p", quantile: 0.9 },
          { text: "P50", value: "p50", type: "p", quantile: 0.5 },
          { text: "P10", value: "p10", type: "p", quantile: 0.1 },
        ],
      },
      colors: {
        curves: {
          p10: "#F44336",
          p50: "#4CAF50",
          p90: "#2196F3",
          avg: "#B0BEC5",
          sum: "#78909C",
          total: "#B0BEC5",
          w10: "#F44336",
          w50: "#4CAF50",
          w90: "#2196F3",
        },
        lines: {
          history: "#BDBDBD",
          arps1: "#F44336",
          advanced3a: "#2196F3",
          advanced5: "#2196F3",
          multi3a: "#FF9800",
          multi5: "#FF9800",
        },
        text: {
          arps1: "error--text",
          advanced3a: "primary--text",
          advanced5: "primary--text",
          multi3a: "orange--text",
          multi5: "orange--text",
        },
      },
      targets: {
        selected: "oil",
        options: [
          { text: "Oil", value: "oil", units: "bbl", color: "#4CAF50", code: 'green' },
          { text: "Gas", value: "gas", units: "mcf", color: "#F44336", code: 'red' },
          // { text: "Water", value: "water", units: "bbl", color: "#03A9F4", code: 'light-blue' },
        ],
      },
      ratios: {
        data: null,
        selected: "gor",
        options: [
          {
            name: "gor",
            label: "GOR",
            units: "mcf/bbl",
            visible: true,
            color: "#F44336",
            type: "scatter",
            dash: "dot",
            width: 2,
          },
          {
            name: "wor",
            label: "WOR",
            units: "bbl",
            visible: true,
            color: "#9C27B0",
            type: "scatter",
            dash: "dot",
            width: 2,
          },
          {
            name: "wgr",
            label: "WGR",
            units: "bbl/mcf",
            visible: true,
            color: "#FF9800",
            type: "scatter",
            dash: "dot",
            width: 2,
          },
        ],
      },
      colorscales: {
        colors: {
          value: "Portland",
          options: [
            { text: "Red-Blue", value: "RdBu" },
            { text: "Yellow-Red", value: "YlOrRd" },
            { text: "Yellow-Green-Blue", value: "YlGnBu" },
            { text: "Portland", value: "Portland" },
            { text: "Picnic", value: "Picnic" },
            { text: "Jet", value: "Jet" },
            { text: "Hot", value: "Hot" },
            { text: "Electric", value: "Electric" },
            { text: "Blue-Red", value: "Bluered" },
          ],
        },
        feature: {
          value: "production",
          options: [
            { text: "Length of Production", value: "duration" },
            { text: "Total Production", value: "production" },
            { text: "Total EUR", value: "eur" },
          ],
        },
      },
      chart_scales: {
        selected: "linear",
        options: [
          { text: "Linear", value: "linear" },
          { text: "Logarithmic", value: "log" },
        ],
      },
      charts: {
        map: {
          loaded: false,
          data: [],
          layout: {
            autosize: true,
            hovermode: "closest",
            showlegend: false,
            height: 500,
            mapbox: {
              style: null,
              bearing: 0,
              center: {
                lat: 0,
                lon: 0,
              },
              pitch: 0,
              zoom: 8,
            },
            font: {
              family: "Roboto, sans-serif",
              color: "#808080",
            },
            margin: { l: 0, r: 0, b: 0, t: 0, pad: 0 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            title: {
              text: null,
              x: 0.5,
              y: 0.01,
              automargin: true,
            },
          },
          options: {
            mapboxAccessToken: process.env.VUE_APP_MAPBOX_TOKEN,
            // displayModeBar: true,
            displaylogo: false,
            //modeBarButtonsToRemove: ["toImage"],
            scrollZoom: false,
            responsive: true,
          },
        },
        ratio: {
          data: [],
          layout: {
            autosize: true,
            height: 300,
            showlegend: true,
            // legend: { x: 1, y: 1, xanchor: "right", bgcolor: "#FFF" },
            font: { family: "Roboto, sans-serif", color: "#808080" },
            xaxis: { title: { text: "Time (months)" }, dtick: 12 },
            yaxis: { title: { text: "Production" }, type: "log" },
            yaxis2: {
              overlaying: "y",
              side: "right",
              type: "log",
              showgrid: false,
              zeroline: false,
              showline: false,
              autotick: true,
              ticks: "",
              showticklabels: true,
            },
            hovermode: "closest",
            margin: { l: 45, t: 15, r: 15, b: 45 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
          },
          options: {
            // displayModeBar: true,
            displaylogo: false,
            responsive: true,
          },
        },
      },
      inputs: {
        oil_price: 60.0,
        gas_price: 3.0,
        oil_differential: 0.0,
        gas_differential: 0.0,
        initial_capex: 0.0,
        fixed_cost: 0.0,
        discount_rate: 10.0,
        shrinkage: 0.0,
        working_interest: 2.5,
        net_revenue_interest: 1.9,
        severance_tax_oil: 4.6,
        severance_tax_gas: 7.5,
        ad_valorem_tax: 2.5,
        expense_oil: 0.0,
        expense_gas: 0.0,
        expense_water: 0.0,
        aggregation_month: new Date().toISOString().substring(0, 10),
      },
      defaults: {
        oil_price: 60.0,
        gas_price: 3.0,
        oil_differential: 0.0,
        gas_differential: 0.0,
        initial_capex: 0.0,
        fixed_cost: 0.0,
        discount_rate: 10.0,
        shrinkage: 0.0,
        working_interest: 2.5,
        net_revenue_interest: 1.9,
        severance_tax_oil: 4.6,
        severance_tax_gas: 7.5,
        ad_valorem_tax: 2.5,
        expense_oil: 10.0,
        expense_gas: 10.0,
        expense_water: 10.0,
        aggregation_month: new Date().toISOString().substring(0, 10),
      },
      annuals: {
        data: [],
        headers: [
          { value: "year", text: "Year" },
          { value: "well_count", text: "Well Count (N)", align: 'end' },
          { value: "gross_oil", text: "Gross Oil (BBL)", align: 'end' },
          { value: "gross_gas", text: "Gross Gas (MCF)", align: 'end' },
          { value: "gross_water", text: "Gross Water (BBL)", align: 'end' },
          { value: "gross_boe", text: "Gross BOE (BBL)", align: 'end' },
          { value: "net_oil", text: "Net Oil (BBL)", align: 'end' },
          { value: "net_gas", text: "Net Gas (MCF)", align: 'end' },
          { value: "oil_price", text: "Oil Price ($/BBL)", align: 'end' },
          { value: "gas_price", text: "Gas Price ($/MCF)", align: 'end' },
          { value: "oil_differential", text: "Oil Differential ($/BBL)", align: 'end' },
          { value: "gas_differential", text: "Gas Differential ($/MCF)", align: 'end' },
          { value: "realized_oil_price", text: "Realized Oil Price ($/BBL)", align: 'end' },
          { value: "realized_gas_price", text: "Realized Gas Price ($/MCF)", align: 'end' },
          { value: "oil_revenue", text: "Oil Revenue ($)", align: 'end' },
          { value: "gas_revenue", text: "Gas Revenue ($)", align: 'end' },
          { value: "gross_revenue", text: "Gross Revenue ($)", align: 'end' },
          { value: "operating_expenses", text: "Operating Expenses ($)", align: 'end' },
          { value: "severance_tax", text: "Severance Tax ($)", align: 'end' },
          { value: "ad_valorem_tax", text: "Ad Valorem Tax ($)", align: 'end' },
          { value: "pv0", text: "PV0 ($)", align: 'end' },
          { value: "pv10", text: "PV10 ($)", align: 'end' },
          { value: "pv15", text: "PV15 ($)", align: 'end' },
          { value: "pv20", text: "PV20 ($)", align: 'end' },
          { value: "pv30", text: "PV30 ($)", align: 'end' },
          { value: "net_income", text: "Net Income ($)", align: 'end' },
        ],
      },
      single: {
        data: [],
        headers: [
          { value: "Month", text: "Month", cellClass: "text-no-wrap" },
          { value: "well_count", text: "Well Count (N)", align: 'end' },
          { value: "gross_oil", text: "Gross Oil (BBL)", align: 'end' },
          { value: "gross_gas", text: "Gross Gas (MCF)", align: 'end' },
          { value: "gross_water", text: "Gross Water (BBL)", align: 'end' },
          { value: "gross_boe", text: "Gross BOE (BBL)", align: 'end' },
          { value: "net_oil", text: "Net Oil (BBL)", align: 'end' },
          { value: "net_gas", text: "Net Gas (MCF)", align: 'end' },
          { value: "oil_price", text: "Oil Price ($/BBL)", align: 'end' },
          { value: "gas_price", text: "Gas Price ($/MCF)", align: 'end' },
          { value: "oil_differential", text: "Oil Differential ($/BBL)", align: 'end' },
          { value: "gas_differential", text: "Gas Differential ($/MCF)", align: 'end' },
          { value: "realized_oil_price", text: "Realized Oil Price ($/BBL)", align: 'end' },
          { value: "realized_gas_price", text: "Realized Gas Price ($/MCF)", align: 'end' },
          { value: "oil_revenue", text: "Oil Revenue ($)", align: 'end' },
          { value: "gas_revenue", text: "Gas Revenue ($)", align: 'end' },
          { value: "gross_revenue", text: "Gross Revenue ($)", align: 'end' },
          { value: "operating_expenses", text: "Operating Expenses ($)", align: 'end' },
          { value: "severance_tax", text: "Severance Tax ($)", align: 'end' },
          { value: "ad_valorem_tax", text: "Ad Valorem Tax ($)", align: 'end' },
          { value: "pv0", text: "PV0 ($)", align: 'end' },
          { value: "pv10", text: "PV10 ($)", align: 'end' },
          { value: "pv15", text: "PV15 ($)", align: 'end' },
          { value: "pv20", text: "PV20 ($)", align: 'end' },
          { value: "pv30", text: "PV30 ($)", align: 'end' },
          { value: "net_income", text: "Net Income ($)", align: 'end' },
        ],
      },
      wells: {
        data: [],
      },
      sections: {
        selected: null,
        list: []
      },
      columnVisibility: {
        options: [],
        selected: [],
        allColumns: []
      },
    }
  },
  computed: {
    API() {
      // let key = 'name' in this.item.config ? this.item.config.name : this.item.config.api
      return this.item.config.api
    },
    LATLEN() {
      let name = "Lat Len - Horz Disp"
      if (name in this.item.config.features && !!this.item.config.features[name]) {
        return this.item.config.features[name]
      }
      return undefined
    },
    totalWells() {
      if (!this.selectedGroup) return 0
      return this.selectedGroup.wells.length
    },
    selectedGroup() {
      if (!!this.groups.selected) {
        return this.groups.list.find((g) => g.id == this.groups.selected)
      }
      return undefined
    },
    hasLateralLength() {
      if (!!this.item) {
        if ("filters" in this.item.config) {
          return !!this.LATLEN && this.item.config.filters.includes(this.LATLEN)
        }
      }
      return false
    },
    months() {
      if (!this.isEmpty(this.frame.items)) {
        return Math.max(...this.frame.items.Months)
      }
      return 0
    },
    groupColor() {
      if (!!this.selectedGroup) {
        return this.targets.options.find(t => t.value == this.selectedGroup.settings.target).code
      }
      return null
    },
    groupTextColor() {
      if (!!this.selectedGroup) {
        return 'white'
      }
      return null
    },
    groupTechnique() {
      if (!!this.selectedGroup) {
        let T = this.selectedGroup.targets
        let final = []
        this.targets.options.forEach(t => {
          if (t.value in T) {
            if ('curves' in T[t.value]) {
              if (T[t.value]['curves']) {
                final.push('curves')
              }
            }
            else if ('params' in T[t.value]) {
              if (T[t.value]['params']) {
                final.push('params')
              }
            }
          }
        })
        final = [...new Set(final)]
        if (final.length == 1) {
          return final[0]
        }
        return 'forecasts'
      }
      return null
    },
    wellsHeaders() {
      // Dynamic headers based on the API identifier from config
      const apiField = this.item && this.item.config ? this.item.config.api : 'API10'
      return [
        { value: apiField, text: "API", align: "start" },
        { value: "years", text: "Years (N)", align: "end" },
        { value: "gross_oil", text: "Gross Oil (BBL)", align: 'end' },
        { value: "gross_gas", text: "Gross Gas (MCF)", align: 'end' },
        { value: "gross_water", text: "Gross Water (BBL)", align: 'end' },
        { value: "gross_boe", text: "Gross BOE (BBL)", align: 'end' },
        { value: "net_oil", text: "Net Oil (BBL)", align: 'end' },
        { value: "net_gas", text: "Net Gas (MCF)", align: 'end' },
        { value: "oil_price", text: "Oil Price ($/BBL)", align: 'end' },
        { value: "gas_price", text: "Gas Price ($/MCF)", align: 'end' },
        { value: "oil_differential", text: "Oil Differential ($/BBL)", align: 'end' },
        { value: "gas_differential", text: "Gas Differential ($/MCF)", align: 'end' },
        { value: "realized_oil_price", text: "Realized Oil Price ($/BBL)", align: 'end' },
        { value: "realized_gas_price", text: "Realized Gas Price ($/MCF)", align: 'end' },
        { value: "oil_revenue", text: "Oil Revenue ($)", align: 'end' },
        { value: "gas_revenue", text: "Gas Revenue ($)", align: 'end' },
        { value: "gross_revenue", text: "Gross Revenue ($)", align: 'end' },
        { value: "operating_expenses", text: "Operating Expenses ($)", align: 'end' },
        { value: "severance_tax", text: "Severance Tax ($)", align: 'end' },
        { value: "ad_valorem_tax", text: "Ad Valorem Tax ($)", align: 'end' },
        { value: "pv0", text: "PV0 ($)", align: 'end' },
        { value: "pv10", text: "PV10 ($)", align: 'end' },
        { value: "pv15", text: "PV15 ($)", align: 'end' },
        { value: "pv20", text: "PV20 ($)", align: 'end' },
        { value: "pv30", text: "PV30 ($)", align: 'end' },
        { value: "net_income", text: "Net Income ($)", align: 'end' },
      ]
    },
  },
  watch: {
    uuid: function (val, old) {
      this.$router.replace({ hash: val })
      if (val) {
        Predictions.retrieve(val).then(response => {
          this.item = response.data
        })
        // this.technique.options = techniques.data.map((e) => {
        //   return { text: e.name, value: e.slug }
        // })
        // this.technique.selected = this.technique.options[0].value
      } else {
        this.item = null
      }
    },
    item: function (val, old) {
      if (val) {
        // Load groups
        this.groups.list = this.item.groups.filter(g => g.locked && g.saved)
        // Convert item.config to JSON
        if (!this.isObject(this.item.config)) this.item.config = this.json_parse(this.item.config)

        this.setBlankMap()
        this.toggleDarkMode(this.isDark)
      } else {
        this.charts.map.data = []
      }
    },
    "groups.selected": function (val, old) {
      if (!!val) {
        this.setEconomics()
        this.fetchMapData()
        this.fetchEconomics()
      } else {
        this.frame.items = []
        this.selectPointsOn("map")
        this.inputs = this.defaults
        this.annuals.data = []
        this.wells.data = []
      }
    },
    selectedWell: function (val, old) {
      if (!!val) {
        this.fetchSingleWell()
        this.selectPointsOn("map", [val])
      } else {
        this.fetchEconomics()
        this.selectPointsOn("map")
      }
    },
    isDark: function (val) {
      this.toggleDarkMode(val)
    }
  },
  methods: {
    setBlankMap() {
      this.$set(this.charts.map, "data", [{
        type: "scattermapbox",
        text: '',
        lat: [],
        lon: []
      }])
      this.charts.map.layout.mapbox.center.lat = 0
      this.charts.map.layout.mapbox.center.lon = 0
      this.charts.map.layout.mapbox.zoom = 1
    },
    getMapObject(items, params) {
      return {
        type: "scattermapbox",
        text: items[params.api].map(String),
        lat: items[params.latitude],
        lon: items[params.longitude],
        mode: "markers",
        // hoverinfo: 'none',
        marker: {
          size: 6,
          reversescale: true,
          autocolorscale: true,
        },
        unselected: {
          marker: {
            // size: 12,
            // color: '#607D8B',
            opacity: 0.1
          }
        }
      }
    },
    plotMap() {
      // Set map style
      this.charts.map.layout.mapbox.style = this.isDark
        ? "mapbox://styles/schajee/cla6nsgx3002315o16d3x006u"
        : "mapbox://styles/schajee/cla6np60a001z14mjo36g7dv5"

      // Remove 0,0 coordinates
      const lats = this.frame.items[this.item.config.latitude].filter(x => x != 0)
      const lons = this.frame.items[this.item.config.longitude].filter(x => x != 0)

      // Center align map
      this.charts.map.layout.mapbox.center.lat = this.mean(lats)
      this.charts.map.layout.mapbox.center.lon = this.mean(lons)

      // Zoom map
      this.charts.map.layout.mapbox.zoom = this.mapZoom(lats, lons)

      // Specify label and units
      this.charts.map.layout.title.text = this.colorscales.feature.options.find((x) => x.value == this.colorscales.feature.value).text
      // this.charts.map.layout.title.text = this.charts.map.layout.title.text.concat(` (in ${this.targetUnits})`)
    },
    mapZoom(lats, lons) {
      const x1 = Math.max(...lats)
      const x2 = Math.min(...lats)

      const y1 = Math.max(...lons)
      const y2 = Math.min(...lons)

      const max_bound = Math.max(Math.abs(x1 - x2), Math.abs(y1 - y2)) * 111
      const zoom = 12.5 - Math.log(max_bound)
      return zoom
    },
    setEconomics() {
      if ('economics' in this.selectedGroup) {
        this.inputs = JSON.parse(JSON.stringify(this.selectedGroup.economics))
      } else {
        this.inputs = JSON.parse(JSON.stringify(this.defaults))
        this.selectedGroup.economics = JSON.parse(JSON.stringify(this.defaults))
      }
    },
    saveGroups() {
      this.selectedGroup.economics = JSON.parse(JSON.stringify(this.inputs))
      this.item.groups = JSON.parse(JSON.stringify(this.groups.list))
      Predictions.patch(this.item.uuid, { groups: this.item.groups })
    },
    setTableHeader() {
      if (!this.thousands) {
        this.annuals.headers.map(h => {
          h.text = h.text.replace('(MBBL)', '(BBL)').replace('(MMCF)', '(MCF)').replace('(M$)', '($)')
        })
        this.wellsHeaders.map(h => {
          h.text = h.text.replace('(MBBL)', '(BBL)').replace('(MMCF)', '(MCF)').replace('(M$)', '($)')
        })
      } else {
        this.annuals.headers.map(h => {
          h.text = h.text.replace('(BBL)', '(MBBL)').replace('(MCF)', '(MMCF)').replace('($)', '(M$)')
        })
        this.wellsHeaders.map(h => {
          h.text = h.text.replace('(BBL)', '(MBBL)').replace('(MCF)', '(MMCF)').replace('($)', '(M$)')
        })
      }
    },
    setColumnOptions(data) {
      if (data && data.length > 0) {
        const columns = Object.keys(data[0])
        this.columnVisibility.allColumns = columns
        this.columnVisibility.options = columns
          .filter(col => col !== 'year' && col !== 'Month' && col !== this.API)
          .map(col => ({
            text: this.formatColumnName(col),
            value: col
          }))
        
        // Always select all columns by default
        this.columnVisibility.selected = this.columnVisibility.options.map(opt => opt.value)
      }
    },
    formatColumnName(col) {
      // Handle special cases
      if (col.includes('bfit')) {
        return col
          .split('_')
          .map(word => word === 'bfit' ? 'BFIT' : word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      }
      
      return col
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    getVisibleHeaders(headers) {
      if (!this.selectedGroup) return headers
      return headers.filter(h => 
        h.value === 'year' || 
        h.value === 'Month' || 
        h.value === this.API ||
        this.columnVisibility.selected.includes(h.value)
      )
    },
    onColumnVisibilityChange(selected) {
      this.columnVisibility.selected = selected
    },
    setAnnualTable(data) {
      this.annuals.data = []
      let length = data.length
      this.annuals.data = data.slice(0, 15)
      this.setColumnOptions(data)
      
      // Define columns that should not be summed (prices/differentials)
      const excludeFromSum = ['oil_price', 'gas_price', 'oil_differential', 'gas_differential', 'realized_oil_price', 'realized_gas_price']
      
      // Create a subtotal row from first 15 rows
      let subtotal = data.slice(0, 15).reduce((acc, row) => {
        Object.entries(row).forEach(([key, value]) => {
          if (!excludeFromSum.includes(key)) {
            acc[key] = (acc[key] || 0) + value
          }
        })
        return acc
      }, {})
      // Round all numeric values to 2 decimal places
      Object.keys(subtotal).forEach(key => {
        if (typeof subtotal[key] === 'number') {
          subtotal[key] = Math.round(subtotal[key] * 100) / 100
        }
      })
      subtotal['year'] = 'Subtotal'
      subtotal['well_count'] = null
      excludeFromSum.forEach(col => subtotal[col] = null)

      let after = {}
      if (length > 15) {
        after = data.slice(15).reduce((acc, row) => {
          Object.entries(row).forEach(([key, value]) => {
            if (!excludeFromSum.includes(key)) {
              acc[key] = (acc[key] || 0) + value
            }
          })
          return acc
        }, {})
        // Round all numeric values to 2 decimal places
        Object.keys(after).forEach(key => {
          if (typeof after[key] === 'number') {
            after[key] = Math.round(after[key] * 100) / 100
          }
        })
        after['year'] = 'After'
        after['well_count'] = null
        excludeFromSum.forEach(col => after[col] = null)
      }

      let total = [subtotal, after].reduce((acc, row) => {
        Object.entries(row).forEach(([key, value]) => {
          if (!excludeFromSum.includes(key)) {
            acc[key] = (acc[key] || 0) + value
          }
        })
        return acc
      }, {})
      // Round all numeric values to 2 decimal places
      Object.keys(total).forEach(key => {
        if (typeof total[key] === 'number') {
          total[key] = Math.round(total[key] * 100) / 100
        }
      })
      total['year'] = 'Total'
      total['well_count'] = null
      excludeFromSum.forEach(col => total[col] = null)

      if (length > 15) {
        this.annuals.data = this.annuals.data.concat(...[subtotal, after, total])
      } else {
        this.annuals.data = this.annuals.data.concat(...[total])
      }
    },
    setWellTable(data) {
      if (!!data) {
        this.wells.data = data
      } else {
        this.wells.data = []
      }
    },
    setFirstTable(data) {
      if (!!data) {
        this.single.data = data
        // Define columns that should not be summed (prices/differentials)
        const excludeFromSum = ['oil_price', 'gas_price', 'oil_differential', 'gas_differential', 'realized_oil_price', 'realized_gas_price']
        
        // Create a subtotal row from first 12 rows
        let subtotal = data.slice(0, 12).reduce((acc, row) => {
          Object.entries(row).forEach(([key, value]) => {
            if (!excludeFromSum.includes(key)) {
              acc[key] = (acc[key] || 0) + value
            }
          })
          return acc
        }, {})
        // Round all numeric values to 2 decimal places
        Object.keys(subtotal).forEach(key => {
          if (typeof subtotal[key] === 'number') {
            subtotal[key] = Math.round(subtotal[key] * 100) / 100
          }
        })
        subtotal['Month'] = 'Total'
        subtotal['well_count'] = null
        excludeFromSum.forEach(col => subtotal[col] = null)
        this.single.data = this.single.data.concat(...[subtotal])
      } else {
        this.single.data = []
      }
    },
    setRatios(data) {
      this.charts.ratio.data = []
      Object.entries(data).forEach(([label, values]) => {
        let trace = {
          x: this.create_array(1, values.length).map((e) => e + 1),
          y: values,
          name: label.toUpperCase(),
          type: "scatter",
          yaxis: label == "gor" ? "y2" : "y1",
          line: {
            color: label == 'gor' ? '#000' : this.targets.options.find((t) => t.value == label).color,
            dash: label == "gor" ? "dot" : "solid",
            width: label == "gor" ? 2 : 1,
          },
        }
        this.charts.ratio.data.push(trace)
      })
    },
    onExportEconomics() {
      var wb = XLSX.utils.book_new()

      // TAB1 - Annual Economics (15-year summary with totals)
      if (!this.isEmpty(this.annuals.data)) {
        var adf = new DataFrame(this.annuals.data)
        var tab1 = [adf.listColumns(), ...adf.toArray()]
        var aws = XLSX.utils.aoa_to_sheet(tab1)
        XLSX.utils.book_append_sheet(wb, aws, "Annual Economics")
      }

      // TAB2 - Well Economics (individual wells)
      if (!this.isEmpty(this.wells.data)) {
        var wdf = new DataFrame(this.wells.data)
        var tab2 = [wdf.listColumns(), ...wdf.toArray()]
        var wws = XLSX.utils.aoa_to_sheet(tab2)
        XLSX.utils.book_append_sheet(wb, wws, "Well Economics")
      }

      // TAB3 - First Year (if single well selected, with total)
      if (!this.isEmpty(this.single.data)) {
        var sdf = new DataFrame(this.single.data)
        var tab3 = [sdf.listColumns(), ...sdf.toArray()]
        var sws = XLSX.utils.aoa_to_sheet(tab3)
        XLSX.utils.book_append_sheet(wb, sws, "First Year")
      }

      // TAB4 - Ratios Data (oil, gas, GOR)
      if (!this.isEmpty(this.charts.ratio.data)) {
        let ratiosData = {}
        this.charts.ratio.data.forEach(trace => {
          ratiosData[trace.name] = trace.y
        })
        var rdf = new DataFrame(ratiosData)
        var tab4 = [rdf.listColumns(), ...rdf.toArray()]
        var rws = XLSX.utils.aoa_to_sheet(tab4)
        XLSX.utils.book_append_sheet(wb, rws, "Ratios")
      }

      // Generate filename
      var filename = this.item.name || this.item.uuid
      filename += this.selectedGroup ? ` - ${this.selectedGroup.name}` : ""
      filename += this.selectedWell ? ` - ${this.selectedWell}` : ""
      filename += " - Economics.xlsb"
      
      // Download file
      XLSX.writeFile(wb, filename)
    },
    onMenuChange(value, variable) {
      this.setPath(this, variable, value)
    },
    selectPointsOn(plot, wells = undefined) {
      if (!this.isEmpty(this.charts[plot].data)) {
        if ("selectedpoints" in this.charts[plot].data[0]) {
          this.$delete(this.charts[plot].data[0], "selectedpoints")
        }
        if (!!wells) {
          if (wells.length < this.totalWells) {
            let indexes = this.charts[plot].data[0].text.map((e, i) => (wells.includes(e) ? i : "")).filter(String)
            this.$set(this.charts[plot].data[0], "selectedpoints", indexes)
          }
        }
      }
    },
    toggleDarkMode(val) {
      if (val) {
        this.$set(this.charts.map.layout.mapbox, "style", "mapbox://styles/schajee/cla6nsgx3002315o16d3x006u")
      } else {
        this.$set(this.charts.map.layout.mapbox, "style", "mapbox://styles/schajee/cla6np60a001z14mjo36g7dv5")
      }
    },
    fetchMapData() {
      this.frame.items = {}
      Economics.get_map_data(this.uuid, this.selectedGroup).then((response) => {
        this.frame.items = response.data
        this.frame.items[this.API] = this.frame.items[this.API].map(String)
        this.$set(this.charts.map, "data", [this.getMapObject(this.frame.items, this.item.config)])
        this.plotMap()
      })
    },
    fetchEconomics() {
      let group = JSON.parse(JSON.stringify(this.selectedGroup))
      Promise.all([
        Economics.get_aggregates(this.uuid, group),
        Economics.get_wells(this.uuid, group),
        Economics.get_ratios(this.uuid, group)
      ]).then(([annual_response, well_response, ratio_response]) => {
        this.setAnnualTable(annual_response.data)
        this.setWellTable(well_response.data)
        this.setRatios(ratio_response.data)
      })
    },
    fetchSingleWell() {
      if (!!this.selectedWell) {
        let group = JSON.parse(JSON.stringify(this.selectedGroup))
        group.wells = [this.selectedWell]
        Promise.all([
          Economics.get_first_year(this.uuid, group),
          Economics.get_aggregates(this.uuid, group),
          Economics.get_ratios(this.uuid, group)
        ]).then(([first_response, annual_response, ratio_response]) => {
          this.setFirstTable(first_response.data)
          this.setAnnualTable(annual_response.data)
          this.setRatios(ratio_response.data)
        })
      } else {
        this.single.data = []
      }
    },
    onChangeInputs() {
      this.saveGroups()
      if (!!this.selectedGroup) {
        this.fetchEconomics()
      }
      if (!!this.selectedWell) {
        this.fetchSingleWell()
      }
    },
    storePaneSize(e) {
      this.$store.commit('setPaneSize', JSON.parse(JSON.stringify(e)))
    },
  },
}
</script>
<style>
.greyscale {
  filter: grayscale(100%);
  opacity: 0.5;
}
</style>